import { useState } from 'react';
import { Check, Loader2, MessageCircle, Pencil, Phone, Search, Truck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { useDriverCombo } from '../../api/driverApi';
import { useFleetDrivers, useUpdateTruckStatus } from '../../features/truck-master/api/truckDriverBookApi';
import type { TruckDriverContact } from '../../features/truck-master/api/truckDriverBookApi';
import { useAssignTruckDriver } from '../../features/truck-master/api/truckMasterFormApi';
import { TRUCK_STATUSES } from '../../features/truck-master/types/truckMasterForm';

interface TruckDriverBookModalProps {
  onClose: () => void;
}

/** `40FT` reads as `40 FT`; anything unknown is shown as stored. */
const sizeLabel = (code: string | null): string => {
  const map: Record<string, string> = {
    '1TON': '1 TON',
    '3TON': '3 TON',
    '5TON': '5 TON',
    '10TON': '10 TON',
    '20FT': '20 FT',
    '40FT': '40 FT',
    SPECIAL: 'SPECIAL',
  };
  return code ? (map[code] ?? code) : '';
};

const statusChip = (status: string | null) => {
  switch (status) {
    case 'WORKSHOP':
      return { label: 'WORKSHOP', className: 'bg-amber-100 text-amber-800' };
    case 'SOLD':
      return { label: 'SOLD', className: 'bg-slate-200 text-slate-600' };
    default:
      return { label: 'ON ROAD', className: 'bg-emerald-100 text-emerald-800' };
  }
};

/**
 * A Malaysian mobile as wa.me wants it: digits only, local 0 swapped for 60.
 * Returns null when there is nothing dialable, so the button stays hidden
 * rather than opening an empty chat.
 */
const whatsappNumber = (mobile: string | null): string | null => {
  if (!mobile) return null;
  const digits = mobile.replace(/[^0-9]/g, '');
  if (digits.length < 8) return null;
  if (digits.startsWith('60')) return digits;
  if (digits.startsWith('0')) return `60${digits.slice(1)}`;
  return digits;
};

/**
 * Truck and driver contact book, opened from the header.
 *
 * Three jobs from one window: find the driver of a truck and call them, change
 * which driver a truck belongs to, and put a truck in the workshop or back on
 * the road. Nothing here deletes anything - a truck keeps all of its history
 * whatever its status, and a released driver keeps theirs.
 *
 * The driver pairing is stored on the driver (DriverMaster.TruckRefId) and the
 * status on the truck, so both changes show up on the Truck Master and Driver
 * Master screens and on the Truck Order Calendar.
 */
export function TruckDriverBookModal({ onClose }: TruckDriverBookModalProps) {
  const { user } = useAuth();
  const companyId = user?.companyId ? Number(user.companyId) : 1;

  const { data: trucks = [], isLoading, isError } = useFleetDrivers(companyId);
  const { data: driverOptions = [] } = useDriverCombo(companyId);
  const assignDriver = useAssignTruckDriver();
  const updateStatus = useUpdateTruckStatus();

  const [search, setSearch] = useState('');
  /** The truck being edited, or null when the list is read-only. */
  const [editingTruckId, setEditingTruckId] = useState<number | null>(null);
  const [draftDriverId, setDraftDriverId] = useState('');
  const [draftStatus, setDraftStatus] = useState('ACTIVE');
  const [draftWorkshopUntil, setDraftWorkshopUntil] = useState('');

  const term = search.trim().toLowerCase();
  const rows = term
    ? trucks.filter((row) =>
        [row.truckName, row.truckNumber, row.truckType, row.driverName, row.driverMobileNo]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term)),
      )
    : trucks;

  const withDriver = trucks.filter((row) => row.driverRefId).length;
  const inWorkshop = trucks.filter((row) => row.truckStatus === 'WORKSHOP').length;
  const saving = assignDriver.isPending || updateStatus.isPending;

  const startEdit = (row: TruckDriverContact) => {
    setEditingTruckId(row.truckId);
    setDraftDriverId(row.driverRefId ? String(row.driverRefId) : '');
    setDraftStatus(row.truckStatus || 'ACTIVE');
    setDraftWorkshopUntil(row.workshopUntil ?? '');
  };

  const cancelEdit = () => {
    setEditingTruckId(null);
    setDraftDriverId('');
    setDraftStatus('ACTIVE');
    setDraftWorkshopUntil('');
  };

  /** Saves whichever of the two actually changed, then closes the row. */
  const save = async (row: TruckDriverContact) => {
    const chosenDriver = Number(draftDriverId) || 0;
    const driverChanged = chosenDriver !== (row.driverRefId ?? 0);
    const statusChanged =
      draftStatus !== (row.truckStatus || 'ACTIVE') ||
      (draftStatus === 'WORKSHOP' && draftWorkshopUntil !== (row.workshopUntil ?? ''));

    if (!driverChanged && !statusChanged) {
      cancelEdit();
      return;
    }

    try {
      const done: string[] = [];

      if (statusChanged) {
        await updateStatus.mutateAsync({
          truckId: row.truckId,
          truckStatus: draftStatus,
          workshopUntil: draftWorkshopUntil || null,
        });
        const label = TRUCK_STATUSES.find((s) => s.code === draftStatus)?.label ?? draftStatus;
        done.push(
          draftStatus === 'WORKSHOP' && draftWorkshopUntil
            ? `in workshop until ${draftWorkshopUntil}`
            : label.toLowerCase(),
        );
      }

      if (driverChanged) {
        await assignDriver.mutateAsync({ truckId: row.truckId, driverRefId: chosenDriver });
        done.push(
          chosenDriver === 0
            ? 'no driver'
            : (driverOptions.find((d: { id?: number | string }) => String(d.id) === draftDriverId)
                ?.name ?? 'driver changed'),
        );
      }

      toast.success(`${row.truckName} → ${done.join(' · ')}`);
      cancelEdit();
    } catch (error) {
      const message = (error as { message?: string })?.message ?? 'Could not save the change';
      toast.error(message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-slate-200 bg-sky-800 px-4 py-3">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-bold text-white">
            <Truck className="h-4.5 w-4.5" aria-hidden />
            Truck &amp; Driver
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold">
              {withDriver} of {trucks.length} assigned
            </span>
            {inWorkshop > 0 && (
              <span className="rounded-full bg-amber-400/90 px-2 py-0.5 text-[11px] font-semibold text-amber-950">
                {inWorkshop} in workshop
              </span>
            )}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </header>

        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search truck, plate or driver…"
              className="w-full rounded-md border border-slate-300 bg-white py-2 pl-8 pr-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
          </label>
        </div>

        <div className="flex-1 overflow-auto">
          {isLoading && (
            <p className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading trucks…
            </p>
          )}

          {isError && (
            <p className="py-10 text-center text-sm text-rose-700">
              Could not load the truck list. Try again in a moment.
            </p>
          )}

          {!isLoading && !isError && (
            <table className="w-full min-w-[860px] border-collapse text-sm">
              <thead className="sticky top-0 bg-slate-100 text-left text-xs text-slate-600">
                <tr>
                  <th scope="col" className="px-3 py-2 font-semibold">Truck</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Type</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Status</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Driver</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Contact</th>
                  <th scope="col" className="px-3 py-2 text-right font-semibold">Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const editing = editingTruckId === row.truckId;
                  const wa = whatsappNumber(row.driverMobileNo);
                  const chip = statusChip(row.truckStatus);
                  return (
                    <tr
                      key={row.truckId}
                      className={[
                        'border-t border-slate-200',
                        editing ? 'bg-sky-50/70' : 'odd:bg-white even:bg-slate-50/60',
                      ].join(' ')}
                    >
                      <td className="px-3 py-2">
                        <span className="block font-semibold text-slate-800">{row.truckName}</span>
                        <span className="block text-[11px] text-slate-500">{row.truckNumber}</span>
                      </td>

                      <td className="px-3 py-2">
                        {row.sizeClass ? (
                          <span className="rounded bg-sky-100 px-2 py-0.5 text-[11px] font-bold text-sky-800">
                            {sizeLabel(row.sizeClass)}
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                        <span className="mt-0.5 block text-[11px] text-slate-500">{row.truckType}</span>
                      </td>

                      {/* ── Status: chip when reading, dropdown when editing ── */}
                      <td className="px-3 py-2">
                        {editing ? (
                          <div className="space-y-1.5">
                            <select
                              value={draftStatus}
                              onChange={(event) => setDraftStatus(event.target.value)}
                              className="w-full rounded-md border border-sky-400 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                            >
                              {TRUCK_STATUSES.map((status) => (
                                <option key={status.code} value={status.code}>
                                  {status.label}
                                </option>
                              ))}
                            </select>
                            {draftStatus === 'WORKSHOP' && (
                              <label className="block">
                                <span className="block text-[10px] font-medium text-slate-500">
                                  Back on the road (optional)
                                </span>
                                <input
                                  type="date"
                                  value={draftWorkshopUntil}
                                  onChange={(event) => setDraftWorkshopUntil(event.target.value)}
                                  className="w-full rounded-md border border-sky-400 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                                />
                              </label>
                            )}
                          </div>
                        ) : (
                          <>
                            <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${chip.className}`}>
                              {chip.label}
                            </span>
                            {row.truckStatus === 'WORKSHOP' && row.workshopUntil && (
                              <span className="mt-0.5 block text-[10px] text-amber-700">
                                back {row.workshopUntil}
                              </span>
                            )}
                          </>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {editing ? (
                          <select
                            value={draftDriverId}
                            onChange={(event) => setDraftDriverId(event.target.value)}
                            className="w-full rounded-md border border-sky-400 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                          >
                            <option value="">No driver</option>
                            {driverOptions.map((driver: { id?: number | string; name?: string }) => (
                              <option key={String(driver.id)} value={String(driver.id)}>
                                {driver.name}
                              </option>
                            ))}
                          </select>
                        ) : row.driverName ? (
                          <span className="font-medium text-slate-800">{row.driverName}</span>
                        ) : (
                          <span className="text-slate-400">Not assigned</span>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {row.driverMobileNo ? (
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="tabular-nums text-slate-700">{row.driverMobileNo}</span>
                            <a
                              href={`tel:${row.driverMobileNo.replace(/\s/g, '')}`}
                              title={`Call ${row.driverName ?? ''}`}
                              className="rounded p-1 text-emerald-700 hover:bg-emerald-50"
                            >
                              <Phone className="h-3.5 w-3.5" aria-hidden />
                              <span className="sr-only">Call</span>
                            </a>
                            {wa && (
                              <a
                                href={`https://wa.me/${wa}`}
                                target="_blank"
                                rel="noreferrer"
                                title="WhatsApp"
                                className="rounded p-1 text-emerald-700 hover:bg-emerald-50"
                              >
                                <MessageCircle className="h-3.5 w-3.5" aria-hidden />
                                <span className="sr-only">WhatsApp</span>
                              </a>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>

                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center justify-end gap-1.5">
                          {editing ? (
                            <>
                              <button
                                type="button"
                                onClick={() => save(row)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:bg-slate-300"
                              >
                                {saving ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                ) : (
                                  <Check className="h-3.5 w-3.5" aria-hidden />
                                )}
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={saving}
                                className="rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startEdit(row)}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden />
                              Change
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                      {term ? `Nothing matches "${search}"` : 'No trucks are set up for booking'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
          One truck keeps one default driver. A truck in the workshop keeps all its history but is
          not offered on the Truck Order Calendar; it comes back on its own the day after the date
          you set. Saving here also updates the Truck Master and Driver Master screens.
        </footer>
      </div>
    </div>
  );
}

export default TruckDriverBookModal;
